import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { myLanes } from '../lib/support'
import { supabase } from '../lib/supabase'
import { initialsOf } from '../lib/initials'
import { messageOf } from '../lib/errors'
import { useHubLang, dateLocaleFor } from '../lib/i18n'
import { SURVEYS } from '../lib/hubCopy'
import { loadOrgUnits, type OrgUnitOption } from '../lib/admin'
import { loadBuPlaces, whereLabel, whereOptions, whereOrgUnitIds, whereTree, type BuPlace } from '../lib/where'
import type { PersonOption } from '../lib/stars'
import {
  ITEM_KINDS,
  answerSurvey,
  answerSurveyItem,
  addSurveyIdea,
  removeSurveyIdea,
  hasFormQuestions,
  IDEAS_MAX,
  type MyIdea,
  answerSummary,
  oneAnswer,
  createSurvey,
  deleteSurvey,
  displayFor,
  draftsFrom,
  hideSurveyAnswer,
  hideSurveyWord,
  loadLiveResults,
  loadMySurveys,
  loadRoleOptions,
  loadSurveyEditor,
  loadSurveyToAnswer,
  missingRequired,
  saveSurvey,
  setSurveyAudience,
  setSurveyLinkAccess,
  groupWords,
  setSurveyPeople,
  setSurveyArchived,
  setSurveyStatus,
  surveyLink,
  type AnswerDraft,
  type AudienceRow,
  type AudienceGroup,
  type EditorItem,
  type ItemKind,
  type LiveResults,
  type SurveyEditor,
  type SurveyPerson,
  type SurveyRow,
  type SurveyStatus,
  type SurveyToAnswer,
  type SurveyItem,
} from '../lib/surveys'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { Dialog } from '../components/Dialog'
import { Picker } from '../components/Picker'
import { PersonField } from '../components/GiveRecognition'
import { SurveyItemResult } from '../components/SurveyResults'
import { ChartIcon, CloseIcon, LockIcon, PlayIcon, QrIcon } from '../components/icons'

/**
 * `#/surveys` and the screens under it (310, David 25 Oct 2026).
 *
 *   #/surveys                  the surveys you were asked, and the ones you run
 *   #/surveys/:id              answer it (the QR code points here)
 *   #/surveys/:id/edit         questions, who is asked, who runs it (owners)
 *   #/surveys/:id/results      every question's answers, live (owners, viewers,
 *                              or everybody asked when the owners shared them)
 *   #/surveys/:id/present      the full-screen display — routes/SurveyPresent
 *
 * Nothing here decides access: each read RAISES for somebody it is not for,
 * and the refusal is shown rather than swallowed (rule 12).
 */
export type SurveysView = 'list' | 'answer' | 'edit' | 'results'

export function Surveys({ me, view }: { me: Me; view: SurveysView }) {
  const { id } = useParams()
  return (
    <AppShell
      current="Surveys"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {view === 'list' && <SurveyList me={me} />}
      {view === 'answer' && id && <SurveyAnswer id={id} />}
      {view === 'edit' && id && <SurveyEdit id={id} me={me} />}
      {view === 'results' && id && <SurveyResultsPage id={id} />}
    </AppShell>
  )
}

function useDate() {
  const lang = useHubLang()
  return (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(dateLocaleFor(lang), { dateStyle: 'medium', timeStyle: 'short' }) : ''
}

/* ── the list ─────────────────────────────────────────────────────────────── */

function SurveyList({ me }: { me: Me }) {
  const t = SURVEYS[useHubLang()]
  const when = useDate()
  const navigate = useNavigate()
  const [rows, setRows] = useState<SurveyRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [making, setMaking] = useState(false)
  // 314. Archived surveys sit folded under the ones you run; deleting asks first.
  const [showArchived, setShowArchived] = useState(false)
  const [deleting, setDeleting] = useState<SurveyRow | null>(null)
  const [rowBusy, setRowBusy] = useState<string | null>(null)

  const reload = useCallback(() => {
    return loadMySurveys()
      .then(setRows)
      .catch((e) => setError(messageOf(e)))
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  async function archive(r: SurveyRow, on: boolean) {
    setRowBusy(r.surveyId)
    setError(null)
    try {
      await setSurveyArchived(r.surveyId, on)
      await reload()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setRowBusy(null)
    }
  }

  const mayMake = can(me, 'survey.manage')
  const open = (rows ?? []).filter((r) => r.invited && r.status === 'open')
  const mine = (rows ?? []).filter((r) => r.myAccess && r.status !== 'archived')
  const archived = (rows ?? []).filter((r) => r.myAccess && r.status === 'archived')
  const closed = (rows ?? []).filter((r) => r.invited && r.status === 'closed' && !r.myAccess)

  const row = (r: SurveyRow, forOwner: boolean) => (
    <li key={r.surveyId} className="surveyrow">
      <Link className="surveyrow__main" to={forOwner && r.myAccess === 'owner' ? `/surveys/${r.surveyId}/edit` : `/surveys/${r.surveyId}`}>
        <span className="surveyrow__title">{r.title}</span>
        {r.description && <span className="surveyrow__desc">{r.description}</span>}
        <span className="surveyrow__meta">
          {r.status === 'draft' && <span className="surveytag surveytag--draft">{t.draftTag}</span>}
          {r.status === 'closed' && <span className="surveytag">{t.closedTag}</span>}
          {r.status === 'archived' && <span className="surveytag">Archived</span>}
          {r.status === 'open' && r.closesAt && <span>{t.closes(when(r.closesAt))}</span>}
          {r.invited && (
            <span className={r.responded ? 'surveytag surveytag--done' : 'surveytag surveytag--todo'}>
              {r.responded ? t.answered : t.notAnswered}
            </span>
          )}
          {forOwner && r.submissions !== null && <span>{t.responses(r.submissions)}</span>}
        </span>
      </Link>
      {forOwner && (
        <span className="surveyrow__acts">
          {r.myAccess === 'owner' && (
            <Link className="btn btn--xs btn--quiet" to={`/surveys/${r.surveyId}/edit`}>Edit</Link>
          )}
          {r.status !== 'draft' && r.status !== 'archived' && (
            <Link className="btn btn--xs btn--quiet" to={`/surveys/${r.surveyId}/present`}>Present</Link>
          )}
          <Link className="btn btn--xs btn--quiet" to={`/surveys/${r.surveyId}/results`}>Results</Link>
          {r.myAccess === 'owner' && r.status === 'closed' && (
            <button type="button" className="btn btn--xs btn--quiet" disabled={rowBusy === r.surveyId} onClick={() => archive(r, true)}>
              Archive
            </button>
          )}
          {r.myAccess === 'owner' && r.status === 'archived' && (
            <button type="button" className="btn btn--xs btn--quiet" disabled={rowBusy === r.surveyId} onClick={() => archive(r, false)}>
              Restore
            </button>
          )}
          {r.myAccess === 'owner' && r.status !== 'open' && (
            <button type="button" className="btn btn--xs btn--link surveys__danger" disabled={rowBusy === r.surveyId} onClick={() => setDeleting(r)}>
              Delete
            </button>
          )}
        </span>
      )}
    </li>
  )

  return (
    <div className="surveys">
      <p className="surveys__lede">{t.lede}</p>
      {error && <p className="surveys__error" role="alert">{error}</p>}
      {!rows && !error && <p className="surveys__muted">{t.loading}</p>}

      {rows && (
        <>
          <Card title={t.openNow}>
            {open.length ? <ul className="surveyrows">{open.map((r) => row(r, false))}</ul> : <p className="surveys__muted">{t.none}</p>}
          </Card>

          {(mayMake || mine.length > 0) && (
            <Card
              title={t.yours}
              action={
                mayMake ? (
                  <button type="button" className="btn btn--sm btn--primary" onClick={() => setMaking(true)}>
                    New survey
                  </button>
                ) : undefined
              }
            >
              {mine.length ? (
                <ul className="surveyrows">{mine.map((r) => row(r, true))}</ul>
              ) : (
                <p className="surveys__muted">
                  {archived.length ? 'None out now.' : 'None yet. A new survey starts as a draft that only you can see.'}
                </p>
              )}
              {archived.length > 0 && (
                <div className="surveyarchived">
                  <button
                    type="button"
                    className="btn btn--sm btn--link"
                    aria-expanded={showArchived}
                    onClick={() => setShowArchived((v) => !v)}
                  >
                    {showArchived ? 'Hide archived' : `Archived (${archived.length})`}
                  </button>
                  {showArchived && <ul className="surveyrows">{archived.map((r) => row(r, true))}</ul>}
                </div>
              )}
            </Card>
          )}

          {closed.length > 0 && (
            <Card title={t.closedHeading}>
              <ul className="surveyrows">{closed.map((r) => row(r, false))}</ul>
            </Card>
          )}
        </>
      )}

      <DeleteSurveyDialog
        survey={deleting && { surveyId: deleting.surveyId, title: deleting.title, responses: deleting.submissions ?? 0 }}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null)
          reload()
        }}
      />

      <NewSurveyDialog
        open={making}
        onClose={() => setMaking(false)}
        onMade={(id) => navigate(`/surveys/${id}/edit`)}
      />
    </div>
  )
}

/** 315. The survey's link, copyable, and its QR code to download for a slide
 *  or a printed sign - the same code the present screen shows. */
function ShareLink({ link, title }: { link: string; title: string }) {
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    let live = true
    import('qrcode')
      .then((m) => ((m as unknown as { default?: typeof m }).default ?? m))
      .then((qrcode) => qrcode.toDataURL(link, { margin: 2, width: 1024, errorCorrectionLevel: 'M', color: { dark: '#002b57', light: '#ffffff' } }))
      .then((url: string) => live && setQr(url))
      .catch(() => live && setQr(null))
    return () => {
      live = false
      window.clearTimeout(timer.current)
    }
  }, [link])

  async function copy() {
    try {
      await navigator.clipboard?.writeText(link)
      setCopied(true)
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  const file = `${(title || 'survey').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '').toLowerCase() || 'survey'}-qr.png`

  return (
    <div className="surveyshare">
      <div className="surveyshare__qr">{qr ? <img src={qr} alt="" /> : <QrIcon size={28} />}</div>
      <div className="surveyshare__body">
        <span className="surveyform__label" id="surveyshare-label">Link for the QR code</span>
        <div className="surveyshare__row">
          <input className="surveyform__input surveyshare__link" readOnly value={link} aria-labelledby="surveyshare-label" onFocus={(e) => e.currentTarget.select()} />
          <button type="button" className="btn btn--quiet" onClick={copy} aria-live="polite">
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        {qr && (
          <a className="surveyshare__dl" href={qr} download={file}>
            Download the QR code (PNG) for a slide or a sign
          </a>
        )}
      </div>
    </div>
  )
}

/** 314. Deleting takes the answers with it, so it asks, and says how many. */
function DeleteSurveyDialog({
  survey,
  onClose,
  onDeleted,
}: {
  survey: { surveyId: string; title: string; responses: number } | null
  onClose: () => void
  onDeleted: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setBusy(false)
    setError(null)
  }, [survey?.surveyId])

  async function go() {
    if (!survey) return
    setBusy(true)
    setError(null)
    try {
      await deleteSurvey(survey.surveyId)
      onDeleted()
    } catch (e) {
      setError(messageOf(e))
      setBusy(false)
    }
  }

  return (
    <Dialog open={!!survey} title="Delete this survey?" onClose={onClose}>
      {survey && (
        <div className="surveyform">
          <p>
            <strong>{survey.title}</strong> and{' '}
            {survey.responses === 0 ? 'its questions' : survey.responses === 1 ? 'its 1 response' : `all ${survey.responses} responses`}{' '}
            will be deleted for good. This cannot be undone.
          </p>
          {survey.responses > 0 && (
            <p className="surveys__muted">To keep the results but take it off everybody's page, archive it instead.</p>
          )}
          {error && <p className="surveys__error" role="alert">{error}</p>}
          <p className="surveyform__actions">
            <button type="button" className="btn btn--danger" disabled={busy} onClick={go}>
              {busy ? 'Deleting…' : 'Delete for good'}
            </button>
            <button type="button" className="btn btn--quiet" disabled={busy} onClick={onClose}>
              Keep it
            </button>
          </p>
        </div>
      )}
    </Dialog>
  )
}

function NewSurveyDialog({ open, onClose, onMade }: { open: boolean; onClose: () => void; onMade: (id: string) => void }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [identified, setIdentified] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function make() {
    setBusy(true)
    setError(null)
    try {
      onMade(await createSurvey(title, description, identified))
    } catch (e) {
      setError(messageOf(e))
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} title="New survey" onClose={onClose}>
      <div className="surveyform">
        <label className="surveyform__field">
          <span className="surveyform__label">Title</span>
          <input className="surveyform__input" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="surveyform__field">
          <span className="surveyform__label">A line about it (optional)</span>
          <input className="surveyform__input" value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <NamesChoice identified={identified} onChange={setIdentified} locked={false} />
        {error && <p className="surveys__error" role="alert">{error}</p>}
        <p className="surveyform__actions">
          <button type="button" className="btn btn--primary" disabled={busy || !title.trim()} onClick={make}>
            {busy ? 'Making…' : 'Make the draft'}
          </button>
        </p>
      </div>
    </Dialog>
  )
}

function NamesChoice({ identified, onChange, locked }: { identified: boolean; onChange: (v: boolean) => void; locked: boolean }) {
  return (
    <fieldset className="surveyform__choice" disabled={locked}>
      <legend className="surveyform__label">Names</legend>
      <label className="surveyform__radio">
        <input type="radio" checked={identified} onChange={() => onChange(true)} />
        <span>
          <strong>Record who answered.</strong> You see each answer with the person's name, and can follow up.
        </span>
      </label>
      <label className="surveyform__radio">
        <input type="radio" checked={!identified} onChange={() => onChange(false)} />
        <span>
          <strong>Anonymous.</strong> No name is kept with an answer, results stay hidden until 6 people answer, and
          each person answers once. A free-text answer can still give its writer away.
        </span>
      </label>
      {locked && <p className="surveys__muted">People have answered, so this cannot change.</p>}
    </fieldset>
  )
}

/** For test/shot-surveys.tsx only: the list without the shell, for a Survey manager. */
export function SurveyListForShot() {
  return <SurveyList me={{ permissions: new Set(['survey.manage']) } as unknown as Me} />
}

/** For test/shot-surveys.tsx only: the editor without the shell. */
export function SurveyEditForShot() {
  const { id } = useParams()
  return id ? <SurveyEdit id={id} me={{ personId: 'p1', permissions: new Set(['survey.manage']) } as unknown as Me} /> : null
}

/** For test/shot-surveys.tsx only: the answer form without the shell. */
export function SurveyAnswerForShot() {
  const { id } = useParams()
  return id ? <SurveyAnswer id={id} /> : null
}

/* ── answering ────────────────────────────────────────────────────────────── */

function SurveyAnswer({ id }: { id: string }) {
  const t = SURVEYS[useHubLang()]
  const when = useDate()
  const [s, setS] = useState<SurveyToAnswer | null>(null)
  const [drafts, setDrafts] = useState<Record<string, AnswerDraft>>({})
  const [error, setError] = useState<string | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [justSent, setJustSent] = useState(false)
  // The live question this phone just sent an answer to: it gets "thanks";
  // a live question answered earlier gets "you already answered this".
  const [sentLive, setSentLive] = useState<string | null>(null)

  // Drafts already typed are kept: a refresh fills only questions not yet in
  // the draft, so polling never wipes what somebody is typing.
  const load = useCallback(() => {
    return loadSurveyToAnswer(id)
      .then((r) => {
        setS(r)
        setDrafts((d) => ({ ...draftsFrom(r), ...d }))
      })
      .catch((e) => setError(messageOf(e)))
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  // 312. While a survey is open, ask again every few seconds, so the phone
  // follows the presenter: the live question appears, changes, and the page
  // goes back to the whole form when the presenter stops running it live.
  const open = s?.status === 'open' && s.mayAnswer
  useEffect(() => {
    if (!open) return
    const h = window.setInterval(() => {
      if (document.hidden) return
      loadSurveyToAnswer(id)
        .then((r) => {
          setS((prev) => {
            if (prev && prev.liveItemId !== r.liveItemId) {
              setEditing(false)
              setSendError(null)
            }
            return r
          })
          setDrafts((d) => ({ ...draftsFrom(r), ...d }))
        })
        .catch(() => {})
    }, 3000)
    return () => window.clearInterval(h)
  }, [open, id])

  if (error) {
    return (
      <div className="surveys">
        <p className="surveys__error" role="alert">{error}</p>
        <p><Link to="/surveys">{t.back}</Link></p>
      </div>
    )
  }
  if (!s) return <p className="surveys__muted">{t.loading}</p>

  const set = (itemId: string, patch: Partial<AnswerDraft>) =>
    setDrafts((d) => ({ ...d, [itemId]: { ...d[itemId], ...patch } }))

  async function send() {
    if (!s) return
    const missing = missingRequired(s.items, drafts)
    if (missing.length) {
      setSendError(t.pleaseAnswer(missing.join(' · ')))
      return
    }
    setBusy(true)
    setSendError(null)
    try {
      await answerSurvey(s.surveyId, s.items, drafts)
      setEditing(false)
      setJustSent(true)
      await load()
    } catch (e) {
      setSendError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const liveItem = s.liveItemId ? s.items.find((i) => i.itemId === s.liveItemId) ?? null : null

  async function sendLive() {
    if (!s || !liveItem) return
    if (!oneAnswer(liveItem, drafts[liveItem.itemId])) {
      setSendError(t.pleaseAnswer(liveItem.prompt))
      return
    }
    setBusy(true)
    setSendError(null)
    try {
      await answerSurveyItem(s.surveyId, liveItem, drafts[liveItem.itemId])
      setEditing(false)
      setSentLive(liveItem.itemId)
      await load()
    } catch (e) {
      setSendError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  // 317: the form is "sent" by its questions that are not ideas; ideas keep coming.
  const showForm = s.mayAnswer && !liveItem && (!s.formSent || editing)
  const ideaItems = s.items.filter((i) => i.kind === 'ideas')
  const formHasQuestions = hasFormQuestions(s.items)
  const liveAnswered = !!liveItem && s.answeredItems.includes(liveItem.itemId)
  const liveMine = liveItem && s.identified ? answerSummary(liveItem, s.myAnswers[liveItem.itemId]) : null

  return (
    <div className="surveys surveys--answer">
      <p className="surveys__back"><Link to="/surveys">← {t.back}</Link></p>
      <h2 className="surveys__title">{s.title}</h2>
      {s.description && !liveItem && <p className="surveys__lede">{s.description}</p>}

      {s.myAccess && (
        <p className="surveyowner">
          {s.myAccess === 'owner' ? 'You run this survey.' : 'You can see this survey’s results.'}{' '}
          {s.myAccess === 'owner' && <Link className="btn btn--xs btn--quiet" to={`/surveys/${id}/edit`}>Edit</Link>}{' '}
          {s.status !== 'draft' && <Link className="btn btn--xs btn--quiet" to={`/surveys/${id}/present`}>Present</Link>}{' '}
          <Link className="btn btn--xs btn--quiet" to={`/surveys/${id}/results`}>Results</Link>
        </p>
      )}

      {s.status === 'draft' && <p className="surveys__muted">{t.draftNote}</p>}
      {s.status === 'closed' && <p className="surveys__muted">{t.closedNote}</p>}
      {s.status === 'open' && s.closesAt && !liveItem && <p className="surveys__muted">{t.closes(when(s.closesAt))}</p>}

      {liveItem && s.mayAnswer && (
        <div className="surveylive">
          <p className="surveylive__tag"><span className="surveylive__dot" aria-hidden="true" /> {t.liveNow}</p>
          {liveItem.kind === 'ideas' ? (
            // 317: a brainstorm - add one idea, then the next; no "thanks" page.
            <IdeasField key={liveItem.itemId} s={s} it={liveItem} onSaved={load} />
          ) : liveAnswered && !editing ? (
            // The question is always named, so a move from one answered
            // question to another is visible (David, 25 Oct: it looked stuck).
            <div className="surveydone" key={liveItem.itemId}>
              <p className="surveylive__prompt">{liveItem.prompt}</p>
              {liveMine && <p className="surveylive__mine">{t.yourAnswer}: <strong>{liveMine}</strong></p>}
              <p className="surveydone__line">{sentLive === liveItem.itemId ? t.liveThanks : t.liveAlready}</p>
              {s.identified && (
                <p className="surveydone__acts">
                  <button type="button" className="btn btn--sm btn--quiet" onClick={() => setEditing(true)}>{t.changeAnswer}</button>
                </p>
              )}
            </div>
          ) : (
            <form
              className="surveyanswer"
              onSubmit={(e) => {
                e.preventDefault()
                sendLive()
              }}
            >
              {!s.answeredItems.length && (
                <p className="surveyanswer__promise">{s.identified ? t.namedNote : t.anonNote}</p>
              )}
              <QuestionField it={liveItem} d={drafts[liveItem.itemId]} set={set} />
              {sendError && <p className="surveys__error" role="alert">{sendError}</p>}
              <p className="surveyform__actions">
                <button type="submit" className="btn btn--primary" disabled={busy}>
                  {busy ? t.sending : t.sendOne}
                </button>
              </p>
            </form>
          )}
        </div>
      )}

      {!liveItem && s.formSent && !editing && (
        <div className="surveydone">
          <p className="surveydone__line">{justSent ? t.thanks : t.answeredOnce}</p>
          <p className="surveydone__acts">
            {s.mayAnswer && s.identified && (
              <button type="button" className="btn btn--sm btn--quiet" onClick={() => setEditing(true)}>{t.change}</button>
            )}
            {s.maySeeResults && <Link className="btn btn--sm btn--quiet" to={`/surveys/${id}/results`}>{t.seeResults}</Link>}
          </p>
        </div>
      )}

      {/* 317: the form is sent, but ideas questions stay open for more. */}
      {!liveItem && s.formSent && !editing && s.mayAnswer && ideaItems.length > 0 && (
        <div className="surveyanswer">
          <p className="surveys__muted">{t.ideasKeepGoing}</p>
          {ideaItems.map((it) => (
            <IdeasField key={it.itemId} s={s} it={it} onSaved={load} />
          ))}
        </div>
      )}

      {showForm && (
        <form
          className="surveyanswer"
          onSubmit={(e) => {
            e.preventDefault()
            send()
          }}
        >
          <p className="surveyanswer__promise">{s.identified ? t.namedNote : t.anonNote}</p>
          {s.items.map((it, n) =>
            it.kind === 'ideas' ? (
              <IdeasField key={it.itemId} s={s} it={it} n={n + 1} onSaved={load} />
            ) : (
              <QuestionField key={it.itemId} it={it} n={n + 1} d={drafts[it.itemId]} set={set} />
            ),
          )}
          {sendError && <p className="surveys__error" role="alert">{sendError}</p>}
          {!formHasQuestions && <p className="surveys__muted">{t.ideasSentAsYouGo}</p>}
          <p className="surveyform__actions">
            {formHasQuestions && (
              <button type="submit" className="btn btn--primary" disabled={busy}>
                {busy ? t.sending : s.formSent ? t.update : t.send}
              </button>
            )}
            {editing && (
              <button type="button" className="btn btn--link" onClick={() => { setEditing(false); setDrafts(draftsFrom(s)) }}>
                Cancel
              </button>
            )}
          </p>
        </form>
      )}
    </div>
  )
}

/** 317. An ideas question: type, press Enter, it is sent - and the box is
 *  ready for the next one. Never a <form> (it sits inside the survey's form),
 *  so Enter is caught here. Named: your ideas as pills you can take back.
 *  Anonymous: only how many you have added - nothing records which are yours. */
function IdeasField({ s, it, n, onSaved }: { s: SurveyToAnswer; it: SurveyItem; n?: number; onSaved: () => unknown }) {
  const t = SURVEYS[useHubLang()]
  const qid = `q-${it.itemId}`
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mine, setMine] = useState<MyIdea[]>(s.myIdeas[it.itemId] ?? [])
  const [count, setCount] = useState(s.myIdeaCounts[it.itemId] ?? 0)
  const input = useRef<HTMLInputElement | null>(null)

  // Follow the server once it has caught up (the page polls every few seconds).
  useEffect(() => {
    setMine(s.myIdeas[it.itemId] ?? [])
    setCount(s.myIdeaCounts[it.itemId] ?? 0)
  }, [s, it.itemId])

  const full = count >= IDEAS_MAX

  async function add() {
    const v = text.trim()
    if (!v || busy || full) return
    setBusy(true)
    setError(null)
    try {
      const answerId = await addSurveyIdea(s.surveyId, it.itemId, v)
      setText('')
      if (answerId) setMine((m) => [...m, { answerId, text: v }])
      setCount((c) => c + 1)
      onSaved()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
      input.current?.focus()
    }
  }

  async function takeBack(idea: MyIdea) {
    setError(null)
    try {
      await removeSurveyIdea(idea.answerId)
      setMine((m) => m.filter((x) => x.answerId !== idea.answerId))
      setCount((c) => Math.max(0, c - 1))
      onSaved()
    } catch (e) {
      setError(messageOf(e))
    }
  }

  return (
    <fieldset className="surveyq surveyideas">
      <legend className="surveyq__prompt" id={qid}>
        {n !== undefined && <span className="surveyq__n">{n}.</span>} {it.prompt}
      </legend>
      <p className="surveyideas__how">{t.ideasHow}</p>
      <div className="surveyideas__row">
        <input
          ref={input}
          className="surveyform__input"
          aria-labelledby={qid}
          value={text}
          maxLength={200}
          placeholder={t.ideaPlaceholder}
          enterKeyHint="send"
          disabled={full}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
        />
        <button type="button" className="btn btn--primary" disabled={busy || full || !text.trim()} onClick={add}>
          {busy ? t.sending : t.addIdea}
        </button>
      </div>
      {error && <p className="surveys__error" role="alert">{error}</p>}
      {s.identified
        ? mine.length > 0 && (
            <ul className="surveypills surveypills--mine" aria-label={t.yourIdeas}>
              {mine.map((m) => (
                <li key={m.answerId} className="surveypill">
                  <span className="surveypill__text">{m.text}</span>
                  <button type="button" className="surveypill__x" aria-label={`${t.takeBack}: ${m.text}`} title={t.takeBack} onClick={() => takeBack(m)}>
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )
        : count > 0 && <p className="surveyideas__count" aria-live="polite">{t.ideasAdded(count)}</p>}
      {full && <p className="surveys__muted">{t.ideasFull(IDEAS_MAX)}</p>}
    </fieldset>
  )
}

/** One question as a form control. The whole form and the live question both
 *  draw it, so a question looks the same either way. */
function QuestionField({
  it,
  n,
  d = { text: '', choices: [], value: null },
  set,
}: {
  it: SurveyItem
  n?: number
  d?: AnswerDraft
  set: (itemId: string, patch: Partial<AnswerDraft>) => void
}) {
  const t = SURVEYS[useHubLang()]
  const qid = `q-${it.itemId}`
  return (
    <fieldset className="surveyq">
      <legend className="surveyq__prompt" id={qid}>
        {n !== undefined && <span className="surveyq__n">{n}.</span>} {it.prompt}
        {it.required && n !== undefined && <span className="surveyq__req"> · {t.required}</span>}
      </legend>
      {it.kind === 'words' && (
        <input
          className="surveyform__input"
          aria-labelledby={qid}
          value={d.text}
          maxLength={80}
          placeholder={t.wordsPlaceholder}
          onChange={(e) => set(it.itemId, { text: e.target.value })}
        />
      )}
      {it.kind === 'text' && (
        <textarea
          className="surveyform__input surveyform__input--area"
          aria-labelledby={qid}
          value={d.text}
          maxLength={2000}
          rows={4}
          placeholder={t.textPlaceholder}
          onChange={(e) => set(it.itemId, { text: e.target.value })}
        />
      )}
      {(it.kind === 'single' || it.kind === 'multi') && (
        <div className="surveyq__choices">
          {it.kind === 'multi' && <p className="surveys__muted">{t.chooseAny}</p>}
          {it.choices.map((c, i) => (
            <label key={i} className="surveyq__choice">
              <input
                type={it.kind === 'single' ? 'radio' : 'checkbox'}
                name={qid}
                checked={d.choices.includes(i)}
                onChange={() =>
                  set(it.itemId, {
                    choices:
                      it.kind === 'single'
                        ? [i]
                        : d.choices.includes(i)
                          ? d.choices.filter((x) => x !== i)
                          : [...d.choices, i].sort((a, b) => a - b),
                  })
                }
              />
              <span>{c}</span>
            </label>
          ))}
        </div>
      )}
      {it.kind === 'scale' && (
        <div className="surveyq__scale">
          <div className="surveyq__points">
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                type="button"
                className={`surveyq__point${d.value === v ? ' surveyq__point--on' : ''}`}
                aria-pressed={d.value === v}
                onClick={() => set(it.itemId, { value: d.value === v ? null : v })}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="surveyq__ends">
            <span>1 · {t.scaleLow}</span>
            <span>5 · {t.scaleHigh}</span>
          </div>
        </div>
      )}
    </fieldset>
  )
}

/* ── results, in the shell ────────────────────────────────────────────────── */

/** Polls every few seconds while the page is open; the screen stays honest
 *  about what it last heard rather than guessing. */
export function useLiveResults(id: string, everyMs = 4000) {
  const [r, setR] = useState<LiveResults | null>(null)
  const [error, setError] = useState<string | null>(null)
  const refresh = useCallback(() => {
    return loadLiveResults(id)
      .then((x) => {
        setR(x)
        setError(null)
      })
      .catch((e) => setError(messageOf(e)))
  }, [id])
  useEffect(() => {
    refresh()
    const h = window.setInterval(() => {
      if (!document.hidden) refresh()
    }, everyMs)
    return () => window.clearInterval(h)
  }, [refresh, everyMs])
  return { results: r, error, refresh }
}

function csvOf(r: LiveResults): string {
  const q = (s: string) => `"${s.replace(/"/g, '""')}"`
  const lines = [['Question', 'Answer', 'Name', 'When', 'Count'].join(',')]
  for (const it of r.items) {
    if (it.answers) {
      for (const a of it.answers) lines.push([q(it.prompt), q(a.text), q(a.name ?? ''), q(a.at ?? ''), ''].join(','))
    } else if (it.counts) {
      it.counts.forEach((n, i) => lines.push([q(it.prompt), q(it.choices[i] ?? ''), '', '', String(n)].join(',')))
    } else if (it.scale) {
      it.scale.forEach((n, i) => lines.push([q(it.prompt), String(i + 1), '', '', String(n)].join(',')))
    }
  }
  return lines.join('\r\n')
}

function SurveyResultsPage({ id }: { id: string }) {
  const t = SURVEYS[useHubLang()]
  const { results: r, error, refresh } = useLiveResults(id, 5000)
  const [names, setNames] = useState(true)
  const owner = r?.myAccess === 'owner'
  const staff = !!r?.myAccess

  function download() {
    if (!r) return
    const blob = new Blob(['﻿' + csvOf(r)], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${r.title.replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') || 'survey'}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  if (error && !r) {
    return (
      <div className="surveys">
        <p className="surveys__error" role="alert">{error}</p>
        <p><Link to="/surveys">{t.back}</Link></p>
      </div>
    )
  }
  if (!r) return <p className="surveys__muted">{t.loading}</p>

  return (
    <div className="surveys">
      <p className="surveys__back"><Link to="/surveys">← {t.back}</Link></p>
      <h2 className="surveys__title">{r.title}</h2>
      <p className="surveyresults__bar">
        <span className="surveyresults__count">{t.responses(r.submissions)}</span>
        {staff && r.status !== 'draft' && <Link className="btn btn--sm btn--quiet" to={`/surveys/${id}/present`}>Present</Link>}
        {owner && <Link className="btn btn--sm btn--quiet" to={`/surveys/${id}/edit`}>Edit</Link>}
        {staff && r.identified && (
          <label className="surveyresults__toggle">
            <input type="checkbox" checked={names} onChange={(e) => setNames(e.target.checked)} /> Show names
          </label>
        )}
        {staff && !r.suppressed && (
          <button type="button" className="btn btn--sm btn--quiet" onClick={download}>Download CSV</button>
        )}
      </p>
      {r.suppressed ? (
        <p className="surveys__muted">{t.waitingFor(r.minToShow)}</p>
      ) : (
        r.items.map((it, n) => (
          <Card key={it.itemId} title={`${n + 1}. ${it.prompt}`}>
            {it.answered !== null && (
              <p className="surveys__muted">
                {it.kind === 'ideas' ? `${t.ideasCount(it.answered)} · ${t.peopleCount(it.people ?? 0)}` : t.answers(it.answered)}
              </p>
            )}
            <SurveyItemResult
              item={it}
              display={it.kind === 'words' ? 'cloud' : undefined}
              showNames={names && staff}
              owner={owner}
              onHideWord={owner ? (w, h) => hideSurveyWord(it.itemId, w, h).then(refresh) : undefined}
              onHideAnswer={owner ? (a, h) => hideSurveyAnswer(a, h).then(refresh) : undefined}
            />
            {it.kind === 'words' && (
              <details className="surveyresults__all">
                <summary>Every answer</summary>
                <SurveyItemResult
                  item={it}
                  display="list"
                  showNames={names && staff}
                  owner={owner}
                  onHideAnswer={owner ? (a, h) => hideSurveyAnswer(a, h).then(refresh) : undefined}
                />
              </details>
            )}
          </Card>
        ))
      )}
    </div>
  )
}

/* ── the editor ───────────────────────────────────────────────────────────── */

const blankItem = (kind: ItemKind = 'words'): EditorItem => ({
  itemId: null,
  kind,
  prompt: '',
  choices: kind === 'single' || kind === 'multi' ? ['', ''] : [],
  required: false,
  display: 'auto',
})

function SurveyEdit({ id, me }: { id: string; me: Me }) {
  const navigate = useNavigate()
  const when = useDate()
  const [ed, setEd] = useState<SurveyEditor | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [identified, setIdentified] = useState(true)
  const [closesAt, setClosesAt] = useState('')
  const [shareResults, setShareResults] = useState(false)
  const [items, setItems] = useState<EditorItem[]>([])
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [actError, setActError] = useState<string | null>(null)

  const load = useCallback(() => {
    return loadSurveyEditor(id)
      .then((e) => {
        setEd(e)
        setTitle(e.title)
        setDescription(e.description ?? '')
        setIdentified(e.identified)
        setClosesAt(e.closesAt ? toLocalInput(e.closesAt) : '')
        setShareResults(e.resultsToAudience)
        setItems(e.items)
        setDirty(false)
      })
      .catch((e) => setError(messageOf(e)))
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  if (error) {
    return (
      <div className="surveys">
        <p className="surveys__error" role="alert">{error}</p>
        <p><Link to="/surveys">All surveys</Link></p>
      </div>
    )
  }
  if (!ed) return <p className="surveys__muted">Loading…</p>

  const answered = new Set(ed.answeredItems)
  const change = (i: number, patch: Partial<EditorItem>) => {
    setItems((xs) => xs.map((x, k) => (k === i ? { ...x, ...patch } : x)))
    setDirty(true)
  }
  const move = (i: number, by: number) => {
    setItems((xs) => {
      const j = i + by
      if (j < 0 || j >= xs.length) return xs
      const c = [...xs]
      ;[c[i], c[j]] = [c[j], c[i]]
      return c
    })
    setDirty(true)
  }

  async function act(label: string, f: () => Promise<unknown>, done?: string) {
    setBusy(label)
    setActError(null)
    setNote(null)
    try {
      await f()
      await load()
      if (done) setNote(done)
    } catch (e) {
      setActError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  const save = () =>
    act(
      'save',
      () =>
        saveSurvey(
          id,
          {
            title,
            description,
            identified,
            closesAt: closesAt ? new Date(closesAt).toISOString() : null,
            resultsToAudience: shareResults,
          },
          items,
        ),
      'Saved.',
    )

  const link = surveyLink(window.location.origin, id)
  const statusLabel = ed.archivedAt ? 'Archived' : ed.status === 'draft' ? 'Draft' : ed.status === 'open' ? 'Open' : 'Closed'
  const statusKey = ed.archivedAt ? 'archived' : ed.status
  const add = (kind: ItemKind) => {
    setItems((xs) => [...xs, blankItem(kind)])
    setDirty(true)
  }

  return (
    <div className="surveys surveys--edit">
      <p className="surveys__back"><Link to="/surveys">← All surveys</Link></p>

      {/* 315. The header: what it is, where it stands, what to do next. */}
      <section className="surveyhead" aria-labelledby="surveyhead-title">
        <div className="surveyhead__top">
          <h1 className="surveyhead__title" id="surveyhead-title">{ed.title || 'Untitled survey'}</h1>
          <span className={`surveystatus surveystatus--${statusKey}`}>
            <span className="surveystatus__dot" aria-hidden="true" /> {statusLabel}
          </span>
        </div>
        {ed.description && <p className="surveyhead__desc">{ed.description}</p>}

        <dl className="surveystats">
          <div className="surveystats__item">
            <dt>Answered</dt>
            <dd>{ed.submissions}</dd>
          </div>
          <div className="surveystats__item">
            <dt>Who can answer</dt>
            <dd className="surveystats__text">
              {ed.invitedCount > 0
                ? `${ed.invitedCount} ${ed.invitedCount === 1 ? 'person' : 'people'}${ed.linkAccess ? ' + the link' : ''}`
                : ed.linkAccess ? 'Anyone with the link' : 'Nobody yet'}
            </dd>
          </div>
          <div className="surveystats__item">
            <dt>{ed.status === 'open' ? 'Closes' : 'Questions'}</dt>
            <dd className="surveystats__text">
              {ed.status === 'open' ? (ed.closesAt ? when(ed.closesAt) : 'When you close it') : items.length}
            </dd>
          </div>
        </dl>

        <div className="surveyhead__acts">
          <span className="surveyhead__main">
            {ed.status === 'open' ? (
              <Link className="btn btn--primary" to={`/surveys/${id}/present`}><PlayIcon size={16} /> Present</Link>
            ) : (
              <button
                type="button"
                className="btn btn--primary"
                disabled={!!busy || dirty}
                title={dirty ? 'Save your changes first' : undefined}
                onClick={() => act('open', () => setSurveyStatus(id, 'open'), 'Open. People who are asked can answer now.')}
              >
                {ed.status === 'closed' ? 'Reopen' : 'Open the survey'}
              </button>
            )}
            {ed.status === 'closed' && <Link className="btn btn--quiet" to={`/surveys/${id}/present`}><PlayIcon size={16} /> Present</Link>}
            <Link className="btn btn--quiet" to={`/surveys/${id}/results`}><ChartIcon size={16} /> Results</Link>
            <Link className="btn btn--link" to={`/surveys/${id}`}>Preview</Link>
          </span>
          <span className="surveyhead__life">
            {ed.status === 'open' && (
              <button type="button" className="btn btn--sm btn--quiet" disabled={!!busy} onClick={() => act('close', () => setSurveyStatus(id, 'closed' as SurveyStatus), 'Closed.')}>
                Close it
              </button>
            )}
            {ed.status === 'open' && ed.submissions === 0 && (
              <button type="button" className="btn btn--sm btn--quiet" disabled={!!busy} onClick={() => act('draft', () => setSurveyStatus(id, 'draft'), 'Back to draft.')}>
                Back to draft
              </button>
            )}
            {ed.archivedAt ? (
              <button type="button" className="btn btn--sm btn--quiet" disabled={!!busy} onClick={() => act('restore', () => setSurveyArchived(id, false), 'Restored. It is back on the page of the people it asked.')}>
                Restore
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--sm btn--quiet"
                disabled={!!busy}
                title="Closes it and takes it off everybody's Surveys page. You keep it, with its results, under Archived."
                onClick={() => act('archive', () => setSurveyArchived(id, true), 'Archived. Only you and the people you share it with still see it.')}
              >
                {ed.status === 'open' ? 'Close and archive' : 'Archive'}
              </button>
            )}
            {ed.status !== 'open' && (
              <button type="button" className="btn btn--sm btn--link surveys__danger" disabled={!!busy} onClick={() => setDeleting(true)}>
                {ed.openedAt ? 'Delete…' : 'Delete draft'}
              </button>
            )}
          </span>
        </div>

        <ShareLink link={link} title={ed.title} />

        {note && <p className="surveys__ok" role="status">{note}</p>}
        {actError && <p className="surveys__error" role="alert">{actError}</p>}
        <DeleteSurveyDialog
          survey={deleting ? { surveyId: id, title: ed.title, responses: ed.submissions } : null}
          onClose={() => setDeleting(false)}
          onDeleted={() => navigate('/surveys')}
        />
      </section>

      <Card title="About this survey">
        <div className="surveyform">
          <label className="surveyform__field">
            <span className="surveyform__label">Title</span>
            <input className="surveyform__input" value={title} maxLength={120} onChange={(e) => { setTitle(e.target.value); setDirty(true) }} />
          </label>
          <label className="surveyform__field">
            <span className="surveyform__label">A line about it (optional)</span>
            <input className="surveyform__input" value={description} maxLength={300} onChange={(e) => { setDescription(e.target.value); setDirty(true) }} />
          </label>
          <NamesChoice identified={identified} onChange={(v) => { setIdentified(v); setDirty(true) }} locked={ed.submissions > 0} />
          <div className="surveyform__pair">
            <label className="surveyform__field">
              <span className="surveyform__label">Closes by itself at (optional)</span>
              <input
                type="datetime-local"
                className="surveyform__input"
                value={closesAt}
                onChange={(e) => { setClosesAt(e.target.value); setDirty(true) }}
              />
            </label>
            <label className="surveyform__check surveyform__check--box">
              <input type="checkbox" checked={shareResults} onChange={(e) => { setShareResults(e.target.checked); setDirty(true) }} />
              <span>Let the people asked see the results (without names)</span>
            </label>
          </div>
        </div>
      </Card>

      <Card title={`Questions · ${items.length}`}>
        <div className="surveyform">
          <ol className="surveyitems">
            {items.map((it, i) => {
              const locked = !!it.itemId && answered.has(it.itemId)
              const kind = ITEM_KINDS.find((k) => k.key === it.kind)
              const choosing = it.kind === 'single' || it.kind === 'multi'
              const textual = it.kind === 'words' || it.kind === 'text'
              return (
                <li key={it.itemId ?? `new-${i}`} className={`surveyitem${locked ? ' surveyitem--locked' : ''}`}>
                  <div className="surveyitem__head">
                    <span className="surveyitem__n" aria-hidden="true">{i + 1}</span>
                    <select
                      className="surveyitem__kind"
                      value={it.kind}
                      disabled={locked}
                      aria-label={`Kind of answer for question ${i + 1}`}
                      onChange={(e) => {
                        const k = e.target.value as ItemKind
                        change(i, { kind: k, choices: (k === 'single' || k === 'multi') && it.choices.length < 2 ? ['', ''] : it.choices, display: 'auto', required: k === 'ideas' ? false : it.required })
                      }}
                    >
                      {ITEM_KINDS.map((k) => (
                        <option key={k.key} value={k.key}>{k.label}</option>
                      ))}
                    </select>
                    <span className="surveyitem__hint">{kind?.hint}</span>
                    {locked && (
                      <span className="surveyitem__lock" title="People have answered it: its kind and its choices so far are kept">
                        <LockIcon size={14} /> Has answers
                      </span>
                    )}
                    <span className="surveyitem__tools">
                      <button type="button" className="surveyitem__tool" aria-label={`Move question ${i + 1} up`} title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                      <button type="button" className="surveyitem__tool" aria-label={`Move question ${i + 1} down`} title="Move down" disabled={i === items.length - 1} onClick={() => move(i, 1)}>↓</button>
                      <button
                        type="button"
                        className="surveyitem__tool surveyitem__tool--x"
                        aria-label={`Remove question ${i + 1}`}
                        disabled={locked}
                        title={locked ? 'It has answers' : 'Remove'}
                        onClick={() => { setItems((xs) => xs.filter((_, k) => k !== i)); setDirty(true) }}
                      >
                        <CloseIcon size={16} />
                      </button>
                    </span>
                  </div>
                  <input
                    className="surveyitem__prompt"
                    placeholder="Type the question"
                    value={it.prompt}
                    maxLength={300}
                    aria-label={`Question ${i + 1}`}
                    onChange={(e) => change(i, { prompt: e.target.value })}
                  />
                  {choosing && (
                    <ul className="surveyitem__choices">
                      {it.choices.map((c, ci) => (
                        <li key={ci} className="surveyitem__choice">
                          <span className={`surveyitem__mark${it.kind === 'multi' ? ' surveyitem__mark--box' : ''}`} aria-hidden="true" />
                          <input
                            className="surveyform__input"
                            value={c}
                            placeholder={`Choice ${ci + 1}`}
                            maxLength={120}
                            aria-label={`Question ${i + 1}, choice ${ci + 1}`}
                            onChange={(e) => change(i, { choices: it.choices.map((x, k) => (k === ci ? e.target.value : x)) })}
                          />
                          {!(locked && ci < (ed.items.find((x) => x.itemId === it.itemId)?.choices.length ?? 0)) && it.choices.length > 2 && (
                            <button
                              type="button"
                              className="surveyitem__tool surveyitem__tool--x"
                              aria-label={`Remove choice ${ci + 1}`}
                              onClick={() => change(i, { choices: it.choices.filter((_, k) => k !== ci) })}
                            >
                              <CloseIcon size={14} />
                            </button>
                          )}
                        </li>
                      ))}
                      {it.choices.length < 20 && (
                        <li>
                          <button type="button" className="surveyitem__addchoice" onClick={() => change(i, { choices: [...it.choices, ''] })}>
                            + Add a choice
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                  <div className="surveyitem__opts">
                    {it.kind === 'ideas' ? (
                      <span className="surveyitem__hint">Never required: each idea is sent as it is added, up to 30 a person.</span>
                    ) : (
                      <label className="surveyitem__req">
                        <input type="checkbox" checked={it.required} onChange={(e) => change(i, { required: e.target.checked })} /> Required
                      </label>
                    )}
                    {(textual || it.kind === 'ideas') && (
                      <span className="surveyitem__show" role="group" aria-label={`Question ${i + 1} shows as`}>
                        <span className="surveyitem__showl">Shows as</span>
                        {(it.kind === 'ideas' ? (['pills', 'list'] as const) : (['cloud', 'list'] as const)).map((d) => {
                          const on = displayFor(it.kind, it.display) === d
                          return (
                            <button key={d} type="button" className={`surveyitem__seg${on ? ' surveyitem__seg--on' : ''}`} aria-pressed={on} onClick={() => change(i, { display: d, required: it.kind === 'ideas' ? false : it.required })}>
                              {d === 'cloud' ? 'Word cloud' : d === 'pills' ? 'Pills' : 'List'}
                            </button>
                          )
                        })}
                      </span>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
          {items.length === 0 && <p className="surveys__muted">No questions yet. Add the first one below.</p>}
          {items.length < 30 && (
            <div className="surveyadd" role="group" aria-label="Add a question">
              <span className="surveyadd__label">Add a question</span>
              {ITEM_KINDS.map((k) => (
                <button key={k.key} type="button" className="surveyadd__kind" title={k.hint} onClick={() => add(k.key)}>
                  + {k.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* 315. Unsaved changes ride along at the foot of the window, not at the
          end of a long card. */}
      {dirty && (
        <div className="surveysave" role="region" aria-label="Unsaved changes">
          <span className="surveysave__note">You have unsaved changes</span>
          <button type="button" className="btn btn--link" disabled={!!busy} onClick={() => load()}>
            Undo
          </button>
          <button type="button" className="btn btn--primary" disabled={!!busy} onClick={save}>
            {busy === 'save' ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}

      <AudienceCard
        ed={ed}
        onSave={(rows, groups) => act('audience', () => setSurveyAudience(id, rows, groups), 'Saved who is asked.')}
        onLink={(on) => act('link', () => setSurveyLinkAccess(id, on), on ? 'Anyone with the link can answer.' : 'The link alone no longer lets people answer.')}
        busy={!!busy}
      />
      <PeopleCard ed={ed} me={me} onSave={(people) => act('people', () => setSurveyPeople(id, people), 'Saved who runs it.')} busy={!!busy} />
    </div>
  )
}

function toLocalInput(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

function AudienceCard({
  ed,
  onSave,
  onLink,
  busy,
}: {
  ed: SurveyEditor
  onSave: (rows: AudienceRow[], groups: AudienceGroup[]) => void
  onLink: (on: boolean) => void
  busy: boolean
}) {
  const [units, setUnits] = useState<OrgUnitOption[]>([])
  const [places, setPlaces] = useState<BuPlace[]>([])
  const [roles, setRoles] = useState<{ key: string; name: string }[]>([])
  const [wherePick, setWherePick] = useState<string[]>([])
  const [deptPick, setDeptPick] = useState<string[]>([])
  const [person, setPerson] = useState<PersonOption | null>(null)
  const [role, setRole] = useState('')

  useEffect(() => {
    loadOrgUnits().then(setUnits).catch(() => setUnits([]))
    loadBuPlaces().then(setPlaces).catch(() => setPlaces([]))
    loadRoleOptions().then(setRoles).catch(() => setRoles([]))
  }, [])

  // The People and roles Where tree (lib/where.ts): Country > Legal entity >
  // Business unit. Departments are their own filter.
  const tree = useMemo(() => whereTree(units, places), [units, places])
  const whereOpts = useMemo(() => whereOptions(tree), [tree])
  const deptOpts = useMemo(
    () => units.filter((u) => u.type === 'department').map((u) => ({ value: u.orgUnitId, label: u.name })),
    [units],
  )
  const deptName = (id: string) => deptOpts.find((o) => o.value === id)?.label ?? id

  const rows = ed.audience
  const groups = ed.groups
  const everyone = rows.some((r) => r.kind === 'all')
  const saveRows = (next: AudienceRow[]) => onSave(next, groups)
  const add = (r: AudienceRow) =>
    saveRows([...rows.filter((x) => !(x.kind === r.kind && x.orgUnitId === r.orgUnitId && x.personId === r.personId && x.roleKey === r.roleKey)), r])
  const label = (r: AudienceRow) =>
    r.kind === 'all' ? 'Everyone' : r.kind === 'org_unit' ? `${r.label} (and everyone under it)` : r.kind === 'role' ? `Role: ${r.label}` : r.label
  const groupLabel = (g: AudienceGroup) => groupWords(g, (c) => whereLabel(c, whereOpts))

  function addGroup() {
    const whereIds = whereOrgUnitIds(wherePick, tree)
    if (whereIds.length + deptPick.length === 0) return
    onSave(rows, [
      ...groups,
      {
        whereChoices: wherePick,
        whereIds,
        departmentIds: deptPick,
        whereNames: [],
        departmentNames: deptPick.map(deptName),
      },
    ])
    setWherePick([])
    setDeptPick([])
  }

  return (
    <Card title="Who is asked">
      <label className="surveyform__check surveylinkopt">
        <input type="checkbox" checked={ed.linkAccess} disabled={busy} onChange={(e) => onLink(e.target.checked)} />
        <span>
          <strong>Anyone with the link can answer.</strong> Anybody signed in who opens the link or scans the QR code
          can answer. It is not listed on anyone’s Surveys page until they have answered it.
        </span>
      </label>

      <p className="surveys__muted">
        People who match any line below see the survey on their Surveys page once it is open. The people who run it are
        not asked unless a line includes them.
      </p>
      {rows.length + groups.length === 0 ? (
        <p className="surveys__muted">{ed.linkAccess ? 'Nobody is listed; the link is the way in.' : 'Nobody yet.'}</p>
      ) : (
        <ul className="surveychips">
          {groups.map((g, i) => (
            <li key={`g${i}`} className="surveychip">
              {groupLabel(g)}
              <button
                type="button"
                className="surveychip__x"
                aria-label={`Remove ${groupLabel(g)}`}
                disabled={busy}
                onClick={() => onSave(rows, groups.filter((_, k) => k !== i))}
              >
                ×
              </button>
            </li>
          ))}
          {rows.map((r, i) => (
            <li key={`r${i}`} className="surveychip">
              {label(r)}
              <button type="button" className="surveychip__x" aria-label={`Remove ${label(r)}`} disabled={busy} onClick={() => saveRows(rows.filter((_, k) => k !== i))}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="surveyaud">
        {!everyone && (
          <button type="button" className="btn btn--sm btn--quiet" disabled={busy} onClick={() => add({ kind: 'all', orgUnitId: null, personId: null, roleKey: null, label: 'Everyone' })}>
            Ask everyone
          </button>
        )}

        <fieldset className="surveygroup">
          <legend className="surveyform__label">A part of the company</legend>
          <div className="surveygroup__filters">
            <Picker
              label="Where"
              anyLabel="Anywhere"
              searchPlaceholder="Country, entity or business unit"
              options={whereOpts}
              selected={wherePick}
              onChange={setWherePick}
            />
            <span className="surveygroup__and" aria-hidden="true">and</span>
            <Picker
              label="Department"
              anyLabel="Any department"
              searchPlaceholder="Department"
              options={deptOpts}
              selected={deptPick}
              onChange={setDeptPick}
            />
          </div>
          <p className="surveys__muted">
            Choose one or both. Several choices in one filter mean any of them; with both filters, people must match
            both. A country means the legal entities in it today.
          </p>
          <p className="surveyform__actions">
            <button type="button" className="btn btn--sm btn--quiet" disabled={busy || wherePick.length + deptPick.length === 0} onClick={addGroup}>
              Add this group
            </button>
          </p>
        </fieldset>

        <div className="surveyaud__row">
          <PersonField person={person} onPick={setPerson} label="A person" />
          <button
            type="button"
            className="btn btn--sm btn--quiet"
            disabled={busy || !person}
            onClick={() => {
              if (person) add({ kind: 'person', orgUnitId: null, personId: person.personId, roleKey: null, label: person.displayName })
              setPerson(null)
            }}
          >
            Add
          </button>
        </div>
        <div className="surveyaud__row">
          <label className="surveyform__field">
            <span className="surveyform__label">Everyone holding a role</span>
            <select className="surveyform__input" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">Choose…</option>
              {roles.map((r) => (
                <option key={r.key} value={r.key}>{r.name}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn--sm btn--quiet"
            disabled={busy || !role}
            onClick={() => {
              add({ kind: 'role', orgUnitId: null, personId: null, roleKey: role, label: role })
              setRole('')
            }}
          >
            Add
          </button>
        </div>
      </div>
    </Card>
  )
}

function PeopleCard({ ed, me, onSave, busy }: { ed: SurveyEditor; me: Me; onSave: (people: SurveyPerson[]) => void; busy: boolean }) {
  const [person, setPerson] = useState<PersonOption | null>(null)
  const people = ed.people
  const owners = people.filter((p) => p.access === 'owner').length
  const put = (p: SurveyPerson) => onSave([...people.filter((x) => x.personId !== p.personId), p])

  return (
    <Card title="Who runs it">
      <p className="surveys__muted">
        Owners edit, open and close the survey, present it and hide answers. Viewers see the results, with names if the
        survey records them. Neither needs the Survey manager role for this survey.
      </p>
      <ul className="surveypeople">
        {people.map((p) => (
          <li key={p.personId} className="surveypeople__row">
            <span>
              {p.name}
              {p.personId === me.personId ? ' (you)' : ''}
            </span>
            <select
              className="surveyform__input surveypeople__access"
              value={p.access}
              disabled={busy || (p.access === 'owner' && owners === 1)}
              aria-label={`${p.name}: access`}
              onChange={(e) => put({ ...p, access: e.target.value as SurveyPerson['access'] })}
            >
              <option value="owner">Owner</option>
              <option value="viewer">Sees results</option>
            </select>
            <button
              type="button"
              className="btn btn--xs btn--link"
              disabled={busy || (p.access === 'owner' && owners === 1)}
              onClick={() => onSave(people.filter((x) => x.personId !== p.personId))}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="surveyaud__row">
        <PersonField person={person} onPick={setPerson} label="Add a person" />
        <button
          type="button"
          className="btn btn--sm btn--quiet"
          disabled={busy || !person}
          onClick={() => {
            if (person) put({ personId: person.personId, access: 'viewer', name: person.displayName })
            setPerson(null)
          }}
        >
          Add as viewer
        </button>
        <button
          type="button"
          className="btn btn--sm btn--quiet"
          disabled={busy || !person}
          onClick={() => {
            if (person) put({ personId: person.personId, access: 'owner', name: person.displayName })
            setPerson(null)
          }}
        >
          Add as owner
        </button>
      </div>
    </Card>
  )
}
